<template>
  <div class="product-catalogs-container bsp-page">
    <div
      v-if="!loading && currentUser && whatsappBusinessAccount"
      class="content bsp-page__content"
    >
      <BspPageHeader
        :breadcrumbs="catalogBreadcrumbs"
        title="Product catalogs"
        :description="`Manage the Meta commerce catalogs available to ${whatsappBusinessAccount.name}.`"
        eyebrow="Commerce"
        icon="tabler:shopping-bag"
      >
        <template #actions>
          <AButton
            variant="outlined"
            color="blue"
            prepend-icon="tabler:refresh"
            :loading="refreshing"
            :disabled="isMutating"
            @click="refreshCatalogs"
          >
            Refresh
          </AButton>
        </template>
        <template #meta>
          <ATag
            :text="`WABA ${whatsappBusinessAccount.id}`"
            color="info"
            type="secondary"
          />
          <ATag
            :text="canManageCatalogs ? 'BSP administrator' : 'Read-only BSP access'"
            :color="canManageCatalogs ? 'success' : 'info'"
            type="secondary"
          />
        </template>
      </BspPageHeader>

      <section class="bsp-summary-grid" aria-label="Catalog summary">
        <AInformationCard
          title="Accessible catalogs"
          :description="catalogCountLabel"
          icon-name="tabler:packages"
          variant="flat"
        />
        <AInformationCard
          title="Connected catalog"
          :description="associatedCatalog?.name || 'Connection not confirmed by Meta'"
          icon-name="tabler:link"
          variant="flat"
        />
        <AInformationCard
          title="Your BSP access"
          :description="canManageCatalogs ? 'Administrator · Meta asset access required' : 'View and refresh only'"
          icon-name="tabler:shield-lock"
          variant="flat"
        />
      </section>

      <section class="bsp-section bsp-catalog-guidance" aria-labelledby="catalog-permissions-title">
        <div class="bsp-section-heading">
          <div>
            <span class="bsp-section-heading__eyebrow">Before you start</span>
            <h2 id="catalog-permissions-title">Permissions and ownership</h2>
            <p>Meta validates the permissions granted to BSP and access to each catalog. A WABA can have only one connected catalog.</p>
          </div>
        </div>
        <div class="bsp-catalog-guidance__grid">
          <div class="bsp-catalog-guidance__item">
            <AIcon icon="tabler:key" color="primary" size="20" with-container />
            <div>
              <h3>Required Meta permissions</h3>
              <p>
                Catalog management requires access to the catalog asset, <strong>business_management</strong>, and
                <strong>catalog_management</strong>. Connecting a catalog also requires
                <strong>whatsapp_business_management</strong> and
                <strong>whatsapp_business_messaging</strong>.
              </p>
            </div>
          </div>
          <div class="bsp-catalog-guidance__item">
            <AIcon icon="tabler:building-store" color="primary" size="20" with-container />
            <div>
              <h3>Business ownership</h3>
              <p>
                Catalog ownership is resolved directly from Meta. Catalogs owned by another business
                remain read-only, and ownership cannot be transferred from this screen.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="bsp-section" aria-labelledby="catalog-access-title">
        <div class="bsp-section-heading bsp-section-heading--toolbar">
          <div>
            <h2 id="catalog-access-title">Catalog access shared with BSP</h2>
            <p>Link another existing commerce catalog from the business portfolio that owns this WABA. In Meta, keep the catalogs you already share selected and add the new catalogs. BSP preserves the existing WhatsApp connection. The authorization is available to other WABAs of this client in the same portfolio.</p>
            <p>Meta manages the catalog access granted to the connected app. To stop sharing a catalog, open Connected apps in Meta, select the BSP app, and remove only that catalog. Then refresh this page.</p>
            <p>Disconnect from WABA changes the WhatsApp association; it does not revoke the app's access or delete the catalog.</p>
          </div>
          <AButton
            color="blue" prepend-icon="tabler:plus"
            :loading="activeAction === 'authorize'"
            :disabled="writesBlocked || !catalogAuthorizationConfigId"
            @click="authorizeCatalogs"
          >
            Link new catalog
          </AButton>
        </div>
        <p v-if="!catalogAuthorizationConfigId" role="status">Catalog authorization is not configured for this environment.</p>
        <p v-if="catalogAuthorizationError" role="alert" class="bsp-form-error">{{ catalogAuthorizationError }}</p>
        <p v-if="activeAction === 'authorize'" role="status">Complete the selection in Meta. Keep the existing catalogs selected; BSP will verify access before saving the catalog authorization.</p>
        <a :href="metaCatalogAccessUrl" target="_blank" rel="noopener noreferrer" class="bsp-catalog-access-link">
          <AIcon icon="tabler:external-link" color="primary" size="18" />
          Manage access in Meta
        </a>
      </section>

      <section
        v-if="!canManageCatalogs"
        class="bsp-catalog-notice"
        role="status"
        aria-labelledby="catalog-read-only-title"
      >
        <AIcon icon="tabler:eye" color="info" size="20" with-container />
        <div>
          <h2 id="catalog-read-only-title">Read-only access</h2>
          <p>Only users with the admin or superuser role can change catalogs.</p>
        </div>
      </section>

      <section v-if="associatedCatalog" class="bsp-section" aria-labelledby="catalog-visibility-title">
        <div class="bsp-section-heading">
          <div>
            <h2 id="catalog-visibility-title">WhatsApp catalog visibility</h2>
            <p>{{ associatedCatalog.name }} · Control visibility for each number connected to this WABA.</p>
          </div>
        </div>
        <CatalogVisibilitySettings :waba-id="String(route.params.wabaId)" :can-manage="canManageCatalogs"
          :disabled="refreshing || isMutating" :initial-phone-id="String(route.query.phoneNumberId || '')"
          @update:phone-id="rememberCatalogPhone" />
      </section>

      <section class="bsp-section" :aria-busy="refreshing" aria-labelledby="catalog-list-title">
        <div class="bsp-section-heading bsp-section-heading--toolbar">
          <div>
            <div class="bsp-section-heading__title-row">
              <h2 id="catalog-list-title">Meta catalogs</h2>
              <ATag :text="`${catalogs.length}`" color="primary" type="secondary" />
            </div>
            <p>A business can own several catalogs. This WABA can connect only one; disconnect it before connecting another.</p>
          </div>
        </div>

        <AEmptyViewCard
          v-if="loadError"
          icon="tabler:cloud-off"
          title="Catalogs unavailable"
          :description="loadError"
          action-button-label="Try again"
          @action="refreshCatalogs"
        />

        <AEmptyViewCard
          v-else-if="catalogs.length === 0"
          icon="tabler:shopping-bag-off"
          title="No accessible catalogs returned"
          description="An existing catalog may still be connected in Meta. Use Link new catalog to share an existing commerce catalog from this WABA’s business portfolio, then refresh. An empty response does not confirm that the WABA has no catalog."
        />

        <div v-else class="bsp-catalog-grid">
          <v-card
            v-for="catalog in catalogs"
            :key="catalog.id"
            class="bsp-catalog-card"
            variant="flat"
          >
            <div class="bsp-catalog-card__header">
              <div class="bsp-catalog-card__identity">
                <AIcon icon="tabler:shopping-bag" color="primary" size="20" with-container />
                <div>
                  <h3>{{ catalog.name }}</h3>
                  <span>ID {{ catalog.id }}</span>
                </div>
              </div>
              <div class="bsp-catalog-card__tags">
                <ATag
                  v-if="catalog.is_associated"
                  :text="isCatalogOwned(catalog) ? 'Connected' : 'Connected · read-only'"
                  :color="isCatalogOwned(catalog) ? 'success' : 'info'"
                  type="secondary"
                />
                <ATag
                  v-else-if="!isCatalogOwned(catalog)"
                  text="Shared · read-only"
                  color="info"
                  type="secondary"
                />
                <ATag v-else text="Available" color="primary" type="secondary" />
              </div>
            </div>

            <dl class="bsp-catalog-details">
              <div>
                <dt>Owner</dt>
                <dd>{{ catalog.owner_business.name }}</dd>
              </div>
              <div>
                <dt>Vertical</dt>
                <dd>{{ formatVertical(catalog.vertical) }}</dd>
              </div>
              <div>
                <dt>Products</dt>
                <dd>{{ formatCount(catalog.product_count) }}</dd>
              </div>
              <div>
                <dt>Feeds</dt>
                <dd>{{ formatCount(catalog.feed_count) }}</dd>
              </div>
            </dl>

            <div class="bsp-catalog-card__actions">
              <AButton variant="outlined" color="blue" prepend-icon="tabler:packages" :disabled="isMutating" @click="router.push({ name: 'catalog-products', params: { wabaId: route.params.wabaId, catalogId: catalog.id } })">
                View products
              </AButton>
              <AButton
                v-if="canManageCatalogs && isCatalogOwned(catalog)"
                variant="outlined"
                color="blue"
                prepend-icon="tabler:edit"
                :disabled="writesBlocked"
                @click="openEditModal(catalog)"
              >
                Edit
              </AButton>
              <AButton
                v-if="canManageCatalogs && !catalog.is_associated"
                color="blue"
                prepend-icon="tabler:link"
                :disabled="isConnectDisabled(catalog)"
                @click="openConnectModal(catalog)"
              >
                Connect to WABA
              </AButton>
              <AButton v-if="canManageCatalogs && catalog.is_associated" variant="outlined" color="red" prepend-icon="tabler:unlink" :disabled="writesBlocked" @click="openDisconnectModal(catalog)">
                Disconnect from WABA
              </AButton>
            </div>

            <p v-if="!isCatalogOwned(catalog)" class="bsp-catalog-card__help">
              This catalog belongs to another business, so BSP cannot edit it.
            </p>
            <p v-else-if="associatedCatalog && !catalog.is_associated" class="bsp-catalog-card__help">
              Another catalog is already connected. BSP will not replace it automatically.
            </p>
          </v-card>
        </div>
      </section>
    </div>

    <div v-else-if="!loading" class="bsp-empty-page">
      <BspPageHeader
        :breadcrumbs="catalogBreadcrumbs"
        title="Product catalogs"
        description="The WhatsApp Business account is not available."
        eyebrow="Commerce"
        icon="tabler:shopping-bag"
      />
      <AEmptyViewCard
        icon="tabler:alert-circle"
        title="Catalogs could not be loaded"
        :description="loadError || 'Return to the account and try again.'"
        action-button-label="Try again"
        @action="loadPage"
      />
    </div>
    <SpinnerLoader v-else />

    <AModal
      v-model="formModalOpen"
      title="Edit product catalog"
      prepend-title-icon="tabler:edit"
      width="560px"
      :close-disabled="isMutating"
      @on-close="closeFormModal"
    >
      <template #body>
        <form class="bsp-modal-form" @submit.prevent="submitCatalogForm">
          <p>Change the name of this catalog. Meta requires catalog_management and permission to manage this catalog.</p>
          <AInput
            v-model="catalogName"
            label="Catalog name"
            placeholder="E.g. Chile commerce catalog"
            autocomplete="off"
            required
            :disabled="refreshing || isMutating"
          />
          <div class="bsp-catalog-fixed-field">
            <AIcon icon="tabler:category" color="primary" size="18" />
            <div>
              <span>Vertical</span>
              <strong>Commerce</strong>
            </div>
          </div>
          <p class="bsp-help-text">The vertical is fixed when the catalog is created.</p>
          <p v-if="formError" class="bsp-catalog-error" role="alert">{{ formError }}</p>
        </form>
      </template>
      <template #actions>
        <AButton variant="text" :disabled="isMutating" @click="closeFormModal">Cancel</AButton>
        <AButton
          color="blue"
          :loading="activeAction === 'edit'"
          :disabled="!canSubmitCatalog || writesBlocked"
          @click="submitCatalogForm"
        >
          {{ catalogFormButtonLabel }}
        </AButton>
      </template>
    </AModal>

    <AModal
      v-if="catalogToConnect"
      v-model="connectModalOpen"
      title="Connect catalog to WABA"
      prepend-title-icon="tabler:link"
      width="560px"
      :close-disabled="isMutating"
      @on-close="closeConnectModal"
    >
      <template #body>
        <div class="bsp-modal-form">
          <p>
            Connect <strong>{{ catalogToConnect.name }}</strong> to
            <strong>{{ whatsappBusinessAccount?.name }}</strong>?
          </p>
          <div class="bsp-catalog-notice bsp-catalog-notice--compact" role="status">
            <AIcon icon="tabler:info-circle" color="info" size="18" with-container />
            <p>
              A WABA supports one connected catalog. BSP does not replace an existing catalog or
              disconnect it automatically.
            </p>
          </div>
          <p v-if="connectError" class="bsp-catalog-error" role="alert">{{ connectError }}</p>
        </div>
      </template>
      <template #actions>
        <AButton variant="text" :disabled="isMutating" @click="closeConnectModal">Cancel</AButton>
        <AButton
          color="blue"
          prepend-icon="tabler:link"
          :loading="activeAction === 'connect'"
          :disabled="writesBlocked"
          @click="connectCatalog"
        >
          Connect catalog
        </AButton>
      </template>
    </AModal>

    <AModal v-if="catalogToDisconnect" v-model="disconnectModalOpen" title="Disconnect catalog from WABA" prepend-title-icon="tabler:unlink" width="560px" :close-disabled="isMutating" @on-close="closeDisconnectModal">
      <template #body>
        <div class="bsp-modal-form">
          <p>Disconnect <strong>{{ catalogToDisconnect.name }}</strong> from <strong>{{ whatsappBusinessAccount?.name }}</strong>?</p>
          <p>The catalog and its products will be retained in Meta. Products from this catalog will no longer be available through this WABA until a catalog is connected again.</p>
          <p v-if="disconnectError" class="bsp-catalog-error" role="alert">{{ disconnectError }}</p>
        </div>
      </template>
      <template #actions>
        <AButton variant="text" :disabled="isMutating" @click="closeDisconnectModal">Cancel</AButton>
        <AButton color="red" :loading="activeAction === 'disconnect'" :disabled="writesBlocked" @click="disconnectCatalog">Disconnect catalog</AButton>
      </template>
    </AModal>

  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { POSITION, useToast } from 'vue-toastification'
import {
  AButton,
  AEmptyViewCard,
  AIcon,
  AInformationCard,
  AInput,
  AModal,
  ATag
} from '@adereso-ai/vue-components'
import BspPageHeader from '@/components/BspPageHeader.vue'
import CatalogVisibilitySettings from '@/components/CatalogVisibilitySettings.vue'
import SpinnerLoader from '@/components/loaders/SpinnerLoader.vue'
import AccountService from '@/services/AccountService/AccountService'
import { WhatsappBusinessAccount } from '@/services/AccountService/types'
import AuthService from '@/services/AuthService/AuthService'
import { User } from '@/services/AuthService/types'
import MetaService from '@/services/MetaService/MetaService'
import { ProductCatalog } from '@/services/MetaService/types'
import { CatalogLoginSdk, requestCatalogAuthorization } from '@/utils/catalogAuthorization'

declare const FB: CatalogLoginSdk

type CatalogAction = 'edit' | 'connect' | 'disconnect' | 'authorize'

const CATALOG_MANAGER_ROLES = new Set(['admin', 'superuser'])

const router = useRouter()
const route = useRoute()
const toast = useToast()

function rememberCatalogPhone (phoneId: string) {
  if (route.query.phoneNumberId !== phoneId) {
    router.replace({ query: { ...route.query, phoneNumberId: phoneId } })
  }
}

const loading = ref(true)
const refreshing = ref(false)
const loadError = ref('')
const catalogAuthorizationError = ref('')
const catalogAuthorizationConfigId = process.env.VUE_APP_FACEBOOK_CATALOG_CONFIG_ID || ''
let authorizationAbort: AbortController | undefined
let pageActive = true
onBeforeUnmount(() => {
  pageActive = false
  authorizationAbort?.abort()
})
const currentUser = ref<User | undefined>(undefined)
const whatsappBusinessAccount = ref<WhatsappBusinessAccount | undefined>(undefined)
const catalogs = ref<ProductCatalog[]>([])

const formModalOpen = ref(false)
const catalogBeingEdited = ref<ProductCatalog | undefined>(undefined)
const catalogName = ref('')
const formError = ref('')

const catalogToConnect = ref<ProductCatalog | undefined>(undefined)
const connectModalOpen = ref(false)
const connectError = ref('')

const catalogToDisconnect = ref<ProductCatalog | undefined>(undefined)
const disconnectModalOpen = ref(false)
const disconnectError = ref('')

const activeAction = ref<CatalogAction | undefined>(undefined)
const isMutating = computed(() => activeAction.value !== undefined)
const canManageCatalogs = computed(() => {
  return currentUser.value?.roles?.some(role => CATALOG_MANAGER_ROLES.has(role)) ?? false
})
const writesBlocked = computed(() => {
  return !canManageCatalogs.value || loading.value || refreshing.value || isMutating.value
})

const metaCatalogAccessUrl = computed(() => {
  const base = 'https://business.facebook.com/latest/settings/connected_apps/'
  const businessId = whatsappBusinessAccount.value?.owner_business_info?.id
  return businessId ? `${base}?business_id=${encodeURIComponent(businessId)}` : base
})
const associatedCatalog = computed(() => catalogs.value.find(catalog => catalog.is_associated))
const catalogCountLabel = computed(() => {
  return `${catalogs.value.length} catalog${catalogs.value.length === 1 ? '' : 's'}`
})
const catalogBreadcrumbs = computed(() => [
  {
    title: 'WhatsApp Accounts',
    icon: 'tabler:brand-whatsapp',
    to: { name: 'whatsapp-business-accounts' }
  },
  {
    title: whatsappBusinessAccount.value?.name || 'Account',
    to: { name: 'whatsapp-business-account', params: { wabaId: String(route.params.wabaId) } }
  },
  { title: 'Product catalogs', disabled: true }
])
const normalizedCatalogName = computed(() => catalogName.value.trim())
const canSubmitCatalog = computed(() => normalizedCatalogName.value.length > 0)
const catalogFormButtonLabel = computed(() => {
  if (activeAction.value === 'edit') return 'Saving…'
  return 'Save changes'
})

function getErrorMessage (error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}

function sortCatalogs (items: ProductCatalog[]): ProductCatalog[] {
  return [...items].sort((first, second) => first.name.localeCompare(second.name))
}

function replaceCatalog (updatedCatalog: ProductCatalog) {
  catalogs.value = sortCatalogs(
    catalogs.value.map(catalog => catalog.id === updatedCatalog.id ? updatedCatalog : catalog)
  )
}

function formatCount (count: number | null): number | string {
  return count === null ? 'Unknown' : count
}

function formatVertical (vertical: string): string {
  if (vertical.toLowerCase() === 'commerce') return 'Commerce'
  return vertical
}

function isCatalogOwned (catalog: ProductCatalog): boolean {
  return catalog.is_owned === true
}

function canStartCatalogMutation (): boolean {
  return canManageCatalogs.value && !loading.value && !refreshing.value && !isMutating.value
}

function isConnectDisabled (catalog: ProductCatalog): boolean {
  return writesBlocked.value ||
    !isCatalogOwned(catalog) ||
    catalog.is_associated ||
    Boolean(associatedCatalog.value)
}

function toastError (message: string) {
  toast.error(message, { position: POSITION.BOTTOM_LEFT })
}

function toastSuccess (message: string) {
  toast.success(message, { position: POSITION.BOTTOM_LEFT })
}

async function getCurrentUser (): Promise<boolean> {
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value
  if (!currentUser.value) {
    await router.push({ name: 'login', query: route.query })
    return false
  }
  return true
}

async function fetchCatalogs (wabaId: string): Promise<ProductCatalog[]> {
  const metaService = await MetaService.getInstance()
  return sortCatalogs(await metaService.getProductCatalogs(wabaId))
}

async function loadPage () {
  loading.value = true
  loadError.value = ''

  try {
    const authenticated = await getCurrentUser()
    if (!authenticated) return

    const wabaId = route.params.wabaId as string
    if (!wabaId) {
      await router.push({ name: 'home' })
      return
    }

    const accountService = await AccountService.getInstance()
    const [account, productCatalogs] = await Promise.allSettled([
      accountService.getWhatsappBusinessAccount(wabaId),
      fetchCatalogs(wabaId)
    ])
    if (account.status === 'rejected') throw account.reason
    whatsappBusinessAccount.value = account.value
    if (productCatalogs.status === 'rejected') throw productCatalogs.reason
    catalogs.value = productCatalogs.value
  } catch (error) {
    loadError.value = getErrorMessage(error)
    toastError('Error loading product catalogs: ' + loadError.value)
  } finally {
    loading.value = false
  }
}

async function authorizeCatalogs () {
  if (!canStartCatalogMutation() || !catalogAuthorizationConfigId) return
  const wabaId = String(route.params.wabaId || '')
  if (!wabaId) return
  activeAction.value = 'authorize'
  catalogAuthorizationError.value = ''
  authorizationAbort = new AbortController()
  try {
    const code = await requestCatalogAuthorization(
      typeof FB === 'undefined' ? undefined : FB,
      catalogAuthorizationConfigId, authorizationAbort.signal
    )
    if (!code || !pageActive || String(route.params.wabaId) !== wabaId) return
    const metaService = await MetaService.getInstance()
    const updatedCatalogs = await metaService.authorizeProductCatalogs(wabaId, code)
    if (!pageActive || String(route.params.wabaId) !== wabaId) return
    catalogs.value = sortCatalogs(updatedCatalogs)
    loadError.value = ''
    toastSuccess('Catalog access updated. Choose a catalog to connect to this WABA.')
  } catch (error) {
    if (!pageActive || String(route.params.wabaId) !== wabaId) return
    catalogAuthorizationError.value = getErrorMessage(error)
    toastError('Catalog authorization failed: ' + catalogAuthorizationError.value)
  } finally {
    authorizationAbort = undefined
    activeAction.value = undefined
  }
}

async function refreshCatalogs () {
  const wabaId = route.params.wabaId as string
  if (!wabaId || refreshing.value || isMutating.value) return

  refreshing.value = true
  loadError.value = ''
  try {
    catalogs.value = await fetchCatalogs(wabaId)
  } catch (error) {
    loadError.value = getErrorMessage(error)
    toastError('Error refreshing product catalogs: ' + loadError.value)
  } finally {
    refreshing.value = false
  }
}

function openEditModal (catalog: ProductCatalog) {
  if (!canStartCatalogMutation() || !isCatalogOwned(catalog)) return

  catalogBeingEdited.value = catalog
  catalogName.value = catalog.name
  formError.value = ''
  formModalOpen.value = true
}

function closeFormModal () {
  if (isMutating.value) return
  formModalOpen.value = false
  catalogBeingEdited.value = undefined
  catalogName.value = ''
  formError.value = ''
}

async function submitCatalogForm () {
  if (!canStartCatalogMutation() || !canSubmitCatalog.value) return

  const wabaId = route.params.wabaId as string
  if (!wabaId) return

  const catalogToUpdate = catalogBeingEdited.value
  if (!catalogToUpdate || !isCatalogOwned(catalogToUpdate)) return

  let succeeded = false
  activeAction.value = 'edit'
  formError.value = ''

  try {
    const metaService = await MetaService.getInstance()
    const updatedCatalog = await metaService.updateProductCatalog(
      wabaId,
      catalogToUpdate.id,
      { name: normalizedCatalogName.value }
    )
    replaceCatalog(updatedCatalog)
    toastSuccess('Product catalog updated')
    succeeded = true
  } catch (error) {
    formError.value = getErrorMessage(error)
    toastError('Error saving product catalog: ' + formError.value)
  } finally {
    activeAction.value = undefined
    if (succeeded) closeFormModal()
  }
}

function openConnectModal (catalog: ProductCatalog) {
  if (
    !canStartCatalogMutation() ||
    !isCatalogOwned(catalog) ||
    catalog.is_associated ||
    associatedCatalog.value
  ) return

  catalogToConnect.value = catalog
  connectError.value = ''
  connectModalOpen.value = true
}

function closeConnectModal () {
  if (isMutating.value) return
  connectModalOpen.value = false
  catalogToConnect.value = undefined
  connectError.value = ''
}

async function connectCatalog () {
  const selectedCatalog = catalogToConnect.value
  if (
    !canStartCatalogMutation() ||
    !selectedCatalog ||
    !isCatalogOwned(selectedCatalog) ||
    selectedCatalog.is_associated ||
    associatedCatalog.value
  ) return

  const wabaId = route.params.wabaId as string
  if (!wabaId) return

  const catalogId = selectedCatalog.id
  let succeeded = false
  activeAction.value = 'connect'
  connectError.value = ''

  try {
    const metaService = await MetaService.getInstance()
    const connectedCatalog = await metaService.connectProductCatalog(wabaId, catalogId)
    catalogs.value = sortCatalogs(catalogs.value.map(catalog => {
      if (catalog.id === connectedCatalog.id) return connectedCatalog
      return { ...catalog, is_associated: false }
    }))
    toastSuccess('Product catalog connected to the WABA')
    succeeded = true
  } catch (error) {
    connectError.value = getErrorMessage(error)
    toastError('Error connecting product catalog: ' + connectError.value)
  } finally {
    activeAction.value = undefined
    if (succeeded) closeConnectModal()
  }
}

function openDisconnectModal (catalog: ProductCatalog) {
  if (!canStartCatalogMutation() || !catalog.is_associated) return
  catalogToDisconnect.value = catalog
  disconnectError.value = ''
  disconnectModalOpen.value = true
}

function closeDisconnectModal () {
  if (isMutating.value) return
  disconnectModalOpen.value = false
  catalogToDisconnect.value = undefined
  disconnectError.value = ''
}

async function disconnectCatalog () {
  const selected = catalogToDisconnect.value
  if (!canStartCatalogMutation() || !selected?.is_associated) return
  activeAction.value = 'disconnect'
  disconnectError.value = ''
  let succeeded = false
  try {
    const service = await MetaService.getInstance()
    await service.disconnectProductCatalog(String(route.params.wabaId), selected.id)
    catalogs.value = catalogs.value.flatMap(catalog => {
      if (catalog.id !== selected.id) return [catalog]
      return catalog.is_owned ? [{ ...catalog, is_associated: false }] : []
    })
    toastSuccess('Product catalog disconnected from the WABA')
    succeeded = true
  } catch (error) {
    disconnectError.value = getErrorMessage(error)
    toastError('Error disconnecting product catalog: ' + disconnectError.value)
  } finally {
    activeAction.value = undefined
    if (succeeded) closeDisconnectModal()
  }
}

onMounted(async () => {
  await router.isReady()
  await loadPage()
})
</script>

<style scoped lang="scss">
.bsp-catalog-access-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.bsp-catalog-guidance__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.bsp-catalog-guidance__item {
  min-width: 0;
  padding: 14px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: color-mix(in srgb, var(--bsp-brand) 3%, var(--bsp-surface));
  border: 1px solid color-mix(in srgb, var(--bsp-brand) 14%, var(--bsp-border));
  border-radius: 11px;
}

.bsp-catalog-guidance__item > div {
  min-width: 0;
}

.bsp-catalog-guidance__item h3,
.bsp-catalog-notice h2,
.bsp-catalog-card h3 {
  margin: 0;
  color: var(--bsp-text);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.4;
}

.bsp-catalog-guidance__item p,
.bsp-catalog-notice p,
.bsp-catalog-card__help,
.bsp-modal-form > p {
  margin: 4px 0 0;
  color: var(--bsp-text-muted);
  font-size: 13px;
  line-height: 1.55;
}

.bsp-catalog-guidance__item strong {
  color: var(--bsp-text);
  font-weight: 650;
}

.bsp-catalog-notice {
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: var(--bsp-text);
  background: color-mix(in srgb, var(--bsp-brand) 7%, var(--bsp-surface));
  border: 1px solid color-mix(in srgb, var(--bsp-brand) 28%, var(--bsp-border));
  border-radius: 12px;
}

.bsp-catalog-notice--compact {
  padding: 12px;
}

.bsp-catalog-notice--compact p {
  margin: 0;
}

.bsp-catalog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 12px;
}

#app .bsp-catalog-card {
  min-width: 0;
  padding: 16px !important;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: var(--bsp-surface) !important;
  color: var(--bsp-text) !important;
  border: 1px solid var(--bsp-border) !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 14px rgba(0, 40, 125, 0.035) !important;
}

.bsp-catalog-card__header,
.bsp-catalog-card__identity,
.bsp-catalog-card__tags,
.bsp-catalog-card__actions,
.bsp-catalog-fixed-field {
  display: flex;
  align-items: center;
}

.bsp-catalog-card__header {
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.bsp-catalog-card__identity {
  min-width: 0;
  align-items: flex-start;
  gap: 10px;
}

.bsp-catalog-card__identity > div {
  min-width: 0;
}

.bsp-catalog-card__identity h3 {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bsp-catalog-card__identity span {
  display: block;
  margin-top: 2px;
  overflow: hidden;
  color: var(--bsp-text-muted);
  font-size: 11px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bsp-catalog-card__tags {
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.bsp-catalog-details {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.bsp-catalog-details > div {
  min-width: 0;
  padding: 10px 11px;
  background: var(--bsp-surface-muted);
  border: 1px solid color-mix(in srgb, var(--bsp-border) 82%, transparent);
  border-radius: 9px;
}

.bsp-catalog-details dt {
  color: var(--bsp-text-muted);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
}

.bsp-catalog-details dd {
  margin: 3px 0 0;
  overflow: hidden;
  color: var(--bsp-text);
  font-size: 13px;
  font-weight: 650;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bsp-catalog-card__actions {
  padding-top: 14px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid var(--bsp-border);
}

.bsp-catalog-card__help {
  margin: -4px 0 0;
}

.bsp-catalog-fixed-field {
  padding: 11px 12px;
  gap: 10px;
  background: var(--bsp-surface-muted);
  border: 1px solid var(--bsp-border);
  border-radius: 10px;
}

.bsp-catalog-fixed-field > div {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.bsp-catalog-fixed-field span {
  color: var(--bsp-text-muted);
  font-size: 11px;
}

.bsp-catalog-fixed-field strong {
  color: var(--bsp-text);
  font-size: 13px;
}

.bsp-catalog-error {
  margin: 0;
  padding: 10px 12px;
  color: var(--bsp-error);
  background: color-mix(in srgb, var(--bsp-error) 7%, var(--bsp-surface));
  border: 1px solid color-mix(in srgb, var(--bsp-error) 25%, var(--bsp-border));
  border-radius: 9px;
  font-size: 12px;
  line-height: 1.5;
}

.v-theme--dark #app .bsp-catalog-card,
.v-theme--dark .bsp-catalog-guidance__item {
  background: color-mix(in srgb, var(--bsp-surface) 92%, var(--bsp-brand) 8%) !important;
  border-color: color-mix(in srgb, var(--bsp-border) 78%, var(--bsp-brand) 22%) !important;
}

@media (max-width: 760px) {
  .bsp-catalog-guidance__grid,
  .bsp-catalog-grid {
    grid-template-columns: 1fr;
  }

  .bsp-catalog-card__header {
    flex-direction: column;
  }

  .bsp-catalog-card__tags {
    justify-content: flex-start;
  }
}
</style>
